import type { MutableRefObject } from "react";
import { namespaceInsertedToken } from "./namespace-inserted-token";
import type { RichTextEvent } from "@library-elements/text";
import type { FroalaEditorInstance } from "../types";
import {
  handleAiClassification,
  handleAiContent,
  handleAiInsertText,
} from "./ai-handler";
import { handleTextAlign, handleTextDirection } from "./alignment-handler";
import {
  handleBold,
  handleClearFormatting,
  handleItalic,
  handleStrikethrough,
  handleSubscript,
  handleSuperscript,
  handleUnderline,
} from "./formatting-handlers";
import { handleHighlight } from "./highlight-handler";
import {
  handleDynamicVariable,
  handleInsertEmoji,
  handleInsertIcon,
} from "./insert-handler";
import { handleLink } from "./link-handler";
import { handleList } from "./list-handler";
import { handleDeleteRkpi, handleSaveRkpi } from "./rkpi-handler";
import { handleTextAnimation } from "./text-animation-handler";
import {
  handleFontSize,
  handleLineHeight,
  handleTextColor,
  handleTextStyle,
} from "./text-style-handler";

// The design-system selectors that feed these flags can return either a
// boolean or the raw string form depending on how the value was persisted.
// Accept both — downstream `handleLink` already coerces.
type HyperLinkFlag = boolean | string | undefined;
type HyperLinkSettings = {
  isHyperLinkBold: HyperLinkFlag;
  isHyperLinkUnderline: HyperLinkFlag;
  isHyperLinkItalic: HyperLinkFlag;
};

interface ToolbarHandlerContext {
  id: string;
  lastRange: MutableRefObject<Range | null>;
  selectedText: MutableRefObject<string | null>;
  highlightResetTimeout: MutableRefObject<NodeJS.Timeout | null>;
  handleAllSelection: () => boolean;
  refresh: (clearSelection?: boolean) => void;
  // Getter rather than a value snapshot — design-system hyperlink settings
  // can change after the toolbar handler is created (one-time init effect),
  // and we want each `setLink` call to read the latest values.
  getHyperLinkSettings: () => HyperLinkSettings;
}

export const createToolbarHandler = (ctx: ToolbarHandlerContext) => {
  return (e: RichTextEvent, editor: FroalaEditorInstance) => {
    const el = editor.selection?.element() ?? editor.$el?.[0];
    if (!el) return;

    // Save selection state before operation for error recovery
    const savedSel = window.getSelection();
    const savedRange =
      savedSel && savedSel.rangeCount > 0
        ? savedSel.getRangeAt(0).cloneRange()
        : null;

    try {
      switch (e.type) {
        case "setBold":
          handleBold(editor, ctx.handleAllSelection, ctx.refresh);
          break;
        case "setItalic":
          handleItalic(editor, ctx.handleAllSelection, ctx.refresh);
          break;
        case "setUnderline":
          handleUnderline(editor, ctx.handleAllSelection, ctx.refresh);
          break;
        case "setStrikethrough":
          handleStrikethrough(editor, ctx.handleAllSelection, ctx.refresh);
          break;
        case "formatting":
          handleClearFormatting(editor, ctx.handleAllSelection, ctx.refresh);
          break;
        case "subscript":
          handleSubscript(editor, ctx.handleAllSelection, ctx.refresh);
          break;
        case "superscript":
          handleSuperscript(editor, ctx.handleAllSelection, ctx.refresh);
          break;

        case "setFontSize":
          handleFontSize(editor, el, e.data, ctx.handleAllSelection, ctx.refresh);
          break;
        case "setTextColor":
          handleTextColor(editor, el, e.data, ctx.handleAllSelection, ctx.refresh);
          break;

        // Block-level operations — KEEP handleAllSelection
        case "setTextAlign":
          handleTextAlign(editor, e.data, ctx.handleAllSelection, ctx.refresh);
          break;
        case "setTextStyle":
          handleTextStyle(editor, e.data, ctx.handleAllSelection, ctx.refresh);
          break;
        case "setTextDirection":
          handleTextDirection(
            editor,
            e.data,
            ctx.handleAllSelection,
            ctx.refresh,
          );
          break;
        case "setLineHeight":
          handleLineHeight(editor, e.data, ctx.handleAllSelection, ctx.refresh);
          break;
        case "setHighlight":
          handleHighlight(
            editor,
            el,
            e.data,
            ctx.highlightResetTimeout,
            ctx.handleAllSelection,
            ctx.refresh,
          );
          break;
        case "setList":
          handleList(
            editor,
            e.data,
            ctx.lastRange,
            ctx.handleAllSelection,
            ctx.refresh,
          );
          break;

        // Insert operations
        case "setDynamicVariable":
          handleDynamicVariable(
            editor,
            namespaceInsertedToken(ctx.id, e.data),
            ctx.lastRange,
            ctx.refresh,
          );
          break;
        case "setDynamicVariableAt":
          // Variable dropped onto the ACTIVE editor: seed lastRange with the
          // drop caret so handleDynamicVariable inserts the pill there.
          ctx.lastRange.current = e.data.range;
          handleDynamicVariable(
            editor,
            namespaceInsertedToken(ctx.id, e.data.token),
            ctx.lastRange,
            ctx.refresh,
          );
          break;
        case "setLink":
          handleLink(
            editor,
            e.data,
            ctx.lastRange,
            ctx.selectedText,
            ctx.getHyperLinkSettings(),
            ctx.refresh,
          );
          break;
        case "setAIClassification":
          handleAiClassification(editor, e.data, ctx.lastRange, ctx.refresh);
          break;
        case "insertEmoji":
          handleInsertEmoji(editor, e.data, ctx.lastRange, ctx.refresh);
          break;
        case "insertIcon":
          handleInsertIcon(editor, e.data, ctx.lastRange, ctx.refresh);
          break;
        case "saveRkpi":
          handleSaveRkpi(editor, e.data, ctx.lastRange, ctx.refresh);
          break;
        case "deleteRkpi":
          handleDeleteRkpi(editor, ctx.lastRange, ctx.refresh);
          break;
        case "aiInsertText":
          handleAiInsertText(editor, e.data, ctx.lastRange, ctx.refresh);
          break;
        case "ai":
          handleAiContent(editor, e.data, ctx.refresh);
          break;
        case "setTextAnimation":
          handleTextAnimation(editor, e.data, ctx.refresh);
          break;
      }
    } catch (err) {
      console.error(`[Froala] Handler error for ${e.type}:`, err);
      // Attempt to restore selection on failure
      if (savedRange && savedSel) {
        try {
          savedSel.removeAllRanges();
          savedSel.addRange(savedRange);
        } catch {}
      }
      // Still refresh toolbar to keep it in sync
      ctx.refresh();
    }
  };
};
